// Disable eslint because this code was not written with lint in mind, and was ported in. It needs to be fixed at some point.
/* eslint-disable */
import { Authentication, ThirdPartyTokenProvider, Token } from '@/lib/web-auth-core';

/**
 * This is available after an initialize or initializeFromRedirect call has been made.
 */
export let authentication: BrowserAuthentication;

interface BaseInitializeParams {
  clientId: string;
  clientSecret: string;
  staging?: boolean;
  storage?: TokenStorage | null;
}

interface InitializeParams extends BaseInitializeParams {
  onTokenFailure?: () => void;
}

interface InitializeFromRedirectParams extends BaseInitializeParams {
  data: string;
}

/**
 * Initializes a site for managing security credentials.
 *
 * This will return a token and begin ensuring it stays up-to-date.  If this returns null, the user is not logged in.
 *
 * @param params
 * @param params.clientId The site's client ID (comes from go-auth)
 * @param params.clientSecret The client secrets (comes from go-auth)
 * @param params.staging True to enable staging authentication
 * @param params.onTokenFailure Allows one to listen for when a token fails to refresh, meaning, they aren't logged in anymore.
 * @param params.storage
 */
// eslint-disable-next-line @typescript-eslint/no-empty-function
export function initialize({
  clientId,
  clientSecret,
  staging = false,
  onTokenFailure = () => {},
  storage = null,
}: InitializeParams): Promise<Token | null> {
  if (!storage) {
    storage = new LocalStorageToken();
  }
  authentication = new BrowserAuthentication({
    clientId,
    clientSecret,
    staging,
    storage,
    onTokenFailure,
  });
  return authentication.getAndWatchToken();
}

/**
 * Initializes the redirect page of a site that will receive data from login.youversion.com
 *
 * This will return a token when the initialization is complete.  Once it's complete, the user can be redirected to
 * their final location.
 *
 * @param params
 * @param params.clientId The site's client ID (comes from go-auth)
 * @param params.clientSecret The client secrets (comes from go-auth)
 * @param params.data The json string representation of the data from login.youversion.com
 * @param params.staging True to enable staging authentication
 * @param params.storage If nothing is provided, will default to local storage.  This is for the long term storage of the authentication credentials.
 */
export function initializeFromRedirect({
  clientId,
  clientSecret,
  data,
  staging = false,
  storage = null,
}: InitializeFromRedirectParams): Promise<Token | null> {
  if (!storage) {
    storage = new LocalStorageToken();
  }
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  authentication = new BrowserAuthentication({
    clientId,
    clientSecret,
    staging,
    storage,
    onTokenFailure: () => {},
  });
  return authentication.loginFromRedirect(data);
}

/**
 *
 * @param token
 * @param redirectUri
 */
export function redirectWithToken(token: Token, redirectUri: string) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = redirectUri;
  const data = document.createElement('input');
  data.name = 'data';
  data.value = JSON.stringify(token.toStorageItem());
  form.appendChild(data);
  document.getElementsByTagName('body')[0].appendChild(form);
  form.submit();
}

export class StoredTokens {
  _tokens: Array<StoredToken>;
  _current: number | null;

  constructor(tokens: Array<StoredToken>, current: number | null) {
    this._tokens = tokens;
    this._current = current;
  }

  get current(): StoredToken | null {
    if (this._current == null || this._current >= this._tokens.length) {
      return null;
    }
    return this._tokens[this._current];
  }

  get tokens(): Array<StoredToken> {
    return this._tokens;
  }

  setToken(token: StoredToken) {
    this._tokens = this.tokens.filter(t => t.id != token.id);
    this._current = this._tokens.push(token) - 1;
  }

  removeToken(userId: number) {
    const current = this.current;
    const currentUserId = current == null ? -1 : current.id;
    this._tokens = this.tokens.filter(t => t.id != userId);
    this._current = currentUserId == userId ? 0 : this._tokens.findIndex(t => t.id == currentUserId);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toData(): any {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tokens: any = [];
    for (let i = 0; i < this.tokens.length; i++) {
      tokens.push(this.tokens[i].toData());
    }
    return JSON.stringify({
      current: this._current,
      tokens: tokens,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static fromData(tokens: any): StoredTokens {
    const available: Array<StoredToken> = [];
    if (tokens && tokens.tokens) {
      for (let i = 0; i < tokens.tokens.length; i++) {
        const t = tokens.tokens[i];
        available.push(new StoredToken(t.id, t.token));
      }
    }
    return new StoredTokens(available, tokens ? tokens.current : null);
  }
}

export class StoredToken {
  constructor(
    readonly id: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    readonly token: any
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  toData(): any {
    return {
      id: this.id,
      token: this.token,
    };
  }
}

export interface TokenStorage {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clear(): void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getAvailableTokens(): StoredTokens;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getToken(): StoredToken | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  removeToken(userId: number): StoredTokens;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setToken(userId: number, token: any): StoredTokens;
}

class LocalStorageToken implements TokenStorage {
  private maybeUpgrade(): StoredTokens | null {
    const token = localStorage.getItem('yva');
    if (token) {
      const data = JSON.parse(token);
      const current = new StoredToken(data.id, data);
      const storedTokens = new StoredTokens([current], 0);
      localStorage.removeItem('yva');
      localStorage.setItem('yvas', storedTokens.toData());
      return storedTokens;
    }
    return null;
  }

  getAvailableTokens(): StoredTokens {
    if (typeof localStorage === 'undefined') {
      return new StoredTokens([], null);
    }
    const tokens = localStorage.getItem('yvas');
    if (tokens) {
      const data = JSON.parse(tokens);
      return StoredTokens.fromData(data);
    }
    const upgrade = this.maybeUpgrade();
    if (upgrade) {
      return upgrade;
    }
    return new StoredTokens([], null);
  }

  getToken(): StoredToken | null {
    const tokens = this.getAvailableTokens();
    return tokens.current;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setToken(userId: number, token: any): StoredTokens {
    const tokens = this.getAvailableTokens();
    tokens.setToken(new StoredToken(userId, token));
    localStorage.setItem('yvas', tokens.toData());
    return tokens;
  }

  removeToken(userId: number): StoredTokens {
    const tokens = this.getAvailableTokens();
    tokens.removeToken(userId);
    localStorage.setItem('yvas', tokens.toData());
    return tokens;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clear() {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.removeItem('yva');
    localStorage.removeItem('yvas');
  }
}

interface BrowserAuthenticationConstructor {
  clientId: string;
  clientSecret: string;
  onTokenFailure: () => void;
  staging: boolean;
  storage: TokenStorage;
}

interface TokenRedirectParams {
  clientId: string;
  clientSecret: string;
  scopes?: Array<string>;
}

class AvailableTokens {
  constructor(
    readonly current: Token | null,
    readonly tokens: Array<Token>
  ) {}
}

class BrowserAuthentication {
  private readonly authentication: Authentication;
  private readonly storage: TokenStorage;
  private readonly onTokenFailure: () => void;
  private failures = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private refreshHandle: any | null;
  private token: Token | null;

  constructor({ clientId, clientSecret, staging, storage, onTokenFailure }: BrowserAuthenticationConstructor) {
    this.authentication = new Authentication(clientId, clientSecret, staging);
    this.storage = storage;
    this.refreshHandle = null;
    this.onTokenFailure = onTokenFailure;
    this.token = null;
  }

  /**
   * Retrieve a token from storage and begins to ensure it is up-to-date.
   *
   * If the user isn't logged in, it will return null.
   */
  async getAndWatchToken(): Promise<Token | null> {
    if (this.token != null) {
      return this.token;
    }
    const token = await this.getToken();
    if (!token) {
      return null;
    }
    this.watchToken(token);
    return token;
  }

  /**
   * Retrieves a token from storage.
   *
   * If the user isn't logged in, it will return null.
   */
  async getToken(): Promise<Token | null> {
    const storageToken = this.storage.getToken();
    if (!storageToken) {
      return null;
    }
    return await this.authentication.loginFromStorageItem(storageToken.token);
  }

  /**
   * Get the available tokens.
   */
  async getAvailableTokens(): Promise<AvailableTokens> {
    const storageTokens = this.storage.getAvailableTokens();
    if (storageTokens.tokens.length == 0) {
      return new AvailableTokens(null, []);
    }
    const tokens: Array<Token> = [];
    let current: Token | null = null;
    for (let i = 0; i < storageTokens.tokens.length; i++) {
      try {
        const token = await this.authentication.loginFromStorageItem(storageTokens.tokens[i].token, false, true);
        tokens.push(token);
        if (storageTokens._current == i) {
          current = token;
        }
      } catch (e) {
        console.error(e);
      }
    }
    return new AvailableTokens(current, tokens);
  }

  /**
   * Set this token as the active token, should be sourced from getAvailableTokens
   */
  setActiveToken(token: Token) {
    this.destroy();
    this.storeToken(token);
    this.watchToken(token);
  }

  /**
   * Remove this token from the available tokens.
   *
   * If this is the currently active token and there are other tokens available, it will try and set the next token to be active.
   */
  removeToken(token: Token) {
    this.storage.removeToken(token.user.id);
    if (this.token && this.token.user.id == token.user.id) {
      this.destroy();
    }
  }

  /**
   * Generates a token from the data sent from login.youversion.com.
   *
   * @param data The json string representation of what login.youversion.com provides.
   */
  async loginFromRedirect(data: string): Promise<Token> {
    const token = await this.authentication.loginFromStorageItem(JSON.parse(data));
    this.storeToken(token);
    this.watchToken(token);
    return token;
  }

  /**
   * Logs in a user given an email & password.
   *
   * @param email A youversion email address (or username)
   * @param password The account's password
   */
  async loginWithEmail(email: string, password: string): Promise<Token> {
    const token = await this.authentication.loginWithEmail(email, password, [], (token: Token) => {
      this.storeToken(token);
      this.watchToken(token);
    });
    this.storeToken(token);
    this.watchToken(token);
    return token;
  }

  /**
   * Logs in a user given a third party token
   *
   * @param provider A provider that can provide the third party information we need to auth
   */
  async loginWithThirdParty(provider: ThirdPartyTokenProvider): Promise<Token> {
    const token = await this.authentication.loginWithThirdParty(provider, (token: Token) => {
      this.storeToken(token);
      this.watchToken(token);
    });
    this.storeToken(token);
    this.watchToken(token);
    return token;
  }

  /**
   * For use on login.youversion.com.  This will take a currently authenticated user, and return a token that
   * is usable by the provided client ID & client secret.  It will also request that the token have the provided
   * scopes associated with it.
   *
   * With the provided token, login.youversion.com can call "token.toStorageItem()" to generate a json object that
   * can be sent to the provided redirect url.
   *
   * @param params
   * @param params.clientId
   * @param params.clientSecret
   * @param params.scopes
   */
  async loginWithTokenForRedirect({ clientId, clientSecret, scopes = [] }: TokenRedirectParams): Promise<Token> {
    if (this.token == null) {
      throw new Error('Not logged in');
    }
    return await this.token.exchange(clientId, clientSecret, scopes);
  }

  async logout() {
    if (this.refreshHandle != null) {
      clearTimeout(this.refreshHandle);
    }
    this.refreshHandle = null;
    this.storage.clear();
    this.token = null;
    document.cookie = 'yva=; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Strict; Secure';
  }

  private storeToken(token: Token) {
    document.cookie = `yva=${token.token}; path=/; expires=${new Date(
      token.expires
    ).toISOString()}; SameSite=Strict; Secure`;
    if (token.user.id == 0) {
      throw 'user id is required';
    }
    this.storage.setToken(token.user.id, token.toStorageItem());
  }

  private async refreshToken() {
    try {
      if (this.token == null) {
        return;
      }
      this.token = await this.token.refresh();
      this.storeToken(this.token);
      this.watchToken(this.token);
      this.failures = 0;
    } catch (e) {
      this.failures++;
      if (this.failures < 4) {
        // we failed somehow.  let's try again in 3 seconds.
        this.refreshHandle = setTimeout(this.refreshToken.bind(this), 3_000);
      } else {
        this.onTokenFailure();
      }
    }
  }

  private watchToken(token: Token) {
    const delay = token.expires - new Date().getTime();
    if (this.refreshHandle != null) {
      clearTimeout(this.refreshHandle);
    }
    this.token = token;
    this.refreshHandle = setTimeout(this.refreshToken.bind(this), delay);
  }

  destroy() {
    if (this.refreshHandle != null) {
      clearTimeout(this.refreshHandle);
    }
    this.token = null;
  }
}
